import React, { useState, useRef, useEffect } from 'react';
import { X, ArrowRight, Mic, Volume2 } from 'lucide-react';
import SIAOrb from '../shared/SIAOrb';

/**
 * GST SIA Assistant Component
 * Reusable chat assistant for GSTR-1 and GSTR-3B
 * Features: Text chat + Voice input (push-to-talk)
 */
const GSTSIAAssistant = ({ formType = 'GSTR-1', currentSection = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSiaResponding, setIsSiaResponding] = useState(false);
  
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const siaVoiceAudioRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = formType === 'GSTR-3B' 
        ? "Hi! I'm SIA, your GST assistant. I'll help you understand and file GSTR-3B. What would you like to know?"
        : "Hi! I'm SIA, your GST assistant. I'll help you with GSTR-1 filing. Ask me about any section or concept!";
      
      setMessages([{
        id: 1,
        sender: 'sia',
        text: greeting,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, formType]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (siaVoiceAudioRef.current) {
        siaVoiceAudioRef.current.pause();
        siaVoiceAudioRef.current = null;
      }
    };
  }, []);

  // Start voice recording (push-to-talk)
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processVoiceInput(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'sia',
        text: "I couldn't access your microphone. Please allow microphone access and try again.",
        timestamp: new Date()
      }]);
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsSiaResponding(true);
    }
  };

  // Process voice input
  const processVoiceInput = async (audioBlob) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const history = messages.slice(-6).map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }));

        const response = await fetch(`${API_URL}/api/gst-sia/voice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            audio_base64: base64Audio,
            form_type: formType,
            history: history
          })
        });

        const data = await response.json();
        
        if (data.success && data.user_text) {
          setMessages(prev => [...prev, {
            id: Date.now(),
            sender: 'user',
            text: data.user_text,
            timestamp: new Date(),
            isVoice: true
          }]);

          setTimeout(() => {
            setMessages(prev => [...prev, {
              id: Date.now() + 1,
              sender: 'sia',
              text: data.sia_text,
              timestamp: new Date(),
              isVoice: true
            }]);

            if (data.sia_audio) {
              playSiaVoice(data.sia_audio);
            } else {
              setIsSiaResponding(false);
            }
          }, 100);
        } else if (data.sia_text) {
          setMessages(prev => [...prev, {
            id: Date.now(),
            sender: 'sia',
            text: data.sia_text,
            timestamp: new Date()
          }]);
          setIsSiaResponding(false);
        } else {
          setIsSiaResponding(false);
        }
      };
    } catch (err) {
      console.error('Voice processing error:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'sia',
        text: "I couldn't process that. Please try again.",
        timestamp: new Date()
      }]);
      setIsSiaResponding(false);
    }
  };

  // Play SIA's voice response
  const playSiaVoice = (audioBase64) => {
    try {
      if (siaVoiceAudioRef.current) {
        siaVoiceAudioRef.current.pause();
      }

      const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
      siaVoiceAudioRef.current = audio;
      
      audio.onended = () => setIsSiaResponding(false);
      audio.onerror = () => setIsSiaResponding(false);
      audio.play();
    } catch (err) {
      console.error('Audio playback error:', err);
      setIsSiaResponding(false);
    }
  };

  // Context-aware suggestions based on form type
  const getSuggestions = () => {
    if (formType === 'GSTR-3B') {
      return ['What is GSTR-3B?', 'ITC claim kaise?', 'Due date kya hai?', 'Tax calculation'];
    }
    return ['What is GSTR-1?', 'B2B vs B2C?', 'HSN summary help', 'Due dates'];
  };

  // Send text message
  const handleSend = async () => {
    if (!inputValue.trim()) return;
    
    const userMsg = {
      id: messages.length + 1,
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    const userInput = inputValue.trim();
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await fetch(`${API_URL}/api/gst-sia/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userInput,
          form_type: formType,
          history: messages.slice(-6).map(m => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.text
          }))
        })
      });
      
      const data = await response.json();
      
      const aiResponse = {
        id: messages.length + 2,
        sender: 'sia',
        text: data.success ? data.response : "I'm having trouble right now. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
    } catch (err) {
      console.log('SIA chat error:', err);
      const aiResponse = {
        id: messages.length + 2,
        sender: 'sia',
        text: "I couldn't process that. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
    }
    
    setIsTyping(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="gst-sia-assistant">
      {/* Chat Window */}
      <div className={`absolute bottom-20 right-0 w-[380px] transition-all duration-500 ease-out ${
        isOpen 
          ? 'opacity-100 translate-y-0 pointer-events-auto' 
          : 'opacity-0 translate-y-8 pointer-events-none'
      }`}>
        <div className="bg-gradient-to-b from-[#0d1220] to-[#0a0f1c] border border-purple-500/20 rounded-2xl shadow-2xl shadow-purple-500/10 overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-cyan-600/20 p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 p-[2px]">
                  <div className="w-full h-full rounded-full bg-[#0d1220] flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 via-blue-400 to-cyan-300 animate-pulse"></div>
                  </div>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#0d1220]"></div>
              </div>
              <div>
                <h3 className="text-white font-semibold">SIA - GST Helper</h3>
                <p className="text-xs text-gray-400">{formType} Assistant</p>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="ml-auto p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="sia-close-btn"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="h-[320px] overflow-y-auto p-4 space-y-4 custom-scrollbar">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-3 ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.sender === 'sia' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 p-[2px] flex-shrink-0">
                    <div className="w-full h-full rounded-full bg-[#0d1220] flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-400 to-cyan-300"></div>
                    </div>
                  </div>
                )}
                <div className={`max-w-[80%] ${
                  msg.sender === 'user' 
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                    : 'bg-white/5 text-gray-200 border border-white/10'
                } rounded-2xl px-4 py-3 text-sm whitespace-pre-line`}>
                  {msg.isVoice && (
                    <Mic className="w-3 h-3 inline mr-1 opacity-60" />
                  )}
                  {msg.text}
                </div>
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 p-[2px] flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-[#0d1220] flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-400 to-cyan-300"></div>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          <div className="px-4 pb-2">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {getSuggestions().map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInputValue(suggestion);
                    setTimeout(() => handleSend(), 100);
                  }}
                  className="flex-shrink-0 px-3 py-1.5 text-xs bg-white/5 hover:bg-purple-500/20 border border-white/10 hover:border-purple-500/30 rounded-full text-gray-300 hover:text-white transition-all"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-white/10">
            {/* Voice Recording Active Indicator */}
            {isRecording && (
              <div className="mb-3 flex items-center justify-center gap-2 py-2 bg-red-500/10 border border-red-500/30 rounded-xl">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-red-400 text-sm font-medium">Listening... Release to send</span>
              </div>
            )}
            
            {/* SIA Responding Indicator */}
            {isSiaResponding && !isRecording && (
              <div className="mb-3 flex items-center justify-center gap-2 py-2 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                <Volume2 className="w-4 h-4 text-purple-400 animate-pulse" />
                <span className="text-purple-400 text-sm font-medium">SIA is speaking...</span>
              </div>
            )}
            
            <div className="flex gap-2">
              {/* Voice Input Button - Push to Talk */}
              <button
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onMouseLeave={() => isRecording && stopRecording()}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                disabled={isSiaResponding}
                className={`px-4 py-3 rounded-xl transition-all ${
                  isRecording 
                    ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/50' 
                    : isSiaResponding
                      ? 'bg-purple-500/50 cursor-not-allowed'
                      : 'bg-white/10 hover:bg-white/20 border border-white/10 hover:border-cyan-500/50'
                }`}
                data-testid="sia-voice-btn"
                title="Hold to speak"
              >
                {isRecording ? (
                  <Mic className="w-5 h-5 text-white animate-pulse" />
                ) : (
                  <Mic className="w-5 h-5 text-cyan-400" />
                )}
              </button>
              
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder={isRecording ? "Listening..." : "Ask SIA anything..."}
                disabled={isRecording || isSiaResponding}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-purple-500/50 disabled:opacity-50"
                data-testid="sia-chat-input"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isRecording || isSiaResponding}
                className="px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all"
                data-testid="sia-send-btn"
              >
                <ArrowRight className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Voice hint */}
            <p className="text-center text-xs text-gray-500 mt-2">
              Hold <Mic className="w-3 h-3 inline text-cyan-400" /> to speak with SIA
            </p>
          </div>
        </div>
      </div>

      {/* SIA Orb Button */}
      <SIAOrb 
        isSpeaking={isSiaResponding} 
        size="medium" 
        onClick={() => setIsOpen(!isOpen)}
      />

      {/* Notification dot when closed */}
      {!isOpen && messages.length === 0 && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-purple-500 to-cyan-400 rounded-full animate-pulse flex items-center justify-center">
          <span className="text-[8px] text-white font-bold">1</span>
        </div>
      )}
    </div>
  );
};

export default GSTSIAAssistant;
